'use client';

import { singletonHook } from 'react-singleton-hook';
import { useCallback, useContext, useEffect } from 'react';
import { MetricsContext } from '../providers/metrics/MetricsProvider';
import {
	LOGGING_ENABLED_OVERRIDE,
	METRICS_SAMPLING_LOGGING_ENABLED,
} from '../providers/metrics/metrics';
import { dlog } from '../dev';
import { usePageVisibility } from './utils/usePageVisibility';
import useDriftStore from '../stores/DriftStore/useDriftStore';
import useMarketStateStore from '../stores/useMarketStateStore';
import useFeatureFlagStore from 'src/stores/featureFlag/useFeatureFlagStore';
import usePostHogCapture from './posthog/usePostHogCapture';

const METRICS_LOGGING_FREQUENCY = 60_000;

const hasBeenInvisibleRef = { current: false };
const isVisibleRef = { current: true };

const METRICS_CLIENT_UNIQUENESS_ID_STORAGE_KEY = `metrics_id`;

// Metrics that shouldn't be affected by the browser going idle
type IdleAllowedMetricType =
	| 'transaction_timeout'
	| 'transaction_confirmation'
	| 'transaction_error'
	| 'cu_budget_exceeded';

const METRICS_TO_ALLOW_AFTER_IDLE: IdleAllowedMetricType[] = [
	'transaction_timeout',
	'transaction_confirmation',
	'transaction_error',
	'cu_budget_exceeded',
];

const messageBusMetricsEnabled = (oldGrafanaMetricsFlagEnabled: boolean) =>
	oldGrafanaMetricsFlagEnabled &&
	(LOGGING_ENABLED_OVERRIDE ||
		window?.location?.hostname?.includes('app.drift.trade'));

const useLogMetricsToPostHog = () => {
	const metricsMessageBus = useContext(MetricsContext).eventBus;
	const { captureEvent } = usePostHogCapture();

	const isVisible = usePageVisibility();

	const getDriftStore = useDriftStore((s) => s.get);
	const getMarketStateStore = useMarketStateStore((s) => s.get);
	const getFeatureFlagsStore = useFeatureFlagStore((s) => s.get);
	const oldGrafanaMetricsFlagEnabled = useFeatureFlagStore((s) =>
		s.flagEnabled('OLD_GRAFANA_METRICS_TO_POSTHOG_ENABLED')
	);

	const getPrimarySubscriptionMethod = () => {
		const currentlySelectedMarketId = getDriftStore().selectedMarket.marketId;
		const selectedMarketListeningState =
			getMarketStateStore().getSubscriptionStateForMarket(
				currentlySelectedMarketId
			);
		return selectedMarketListeningState?.listeningSelection;
	};

	useEffect(() => {
		isVisibleRef.current = isVisible;

		if (!isVisible && !hasBeenInvisibleRef.current) {
			dlog(
				`ui_metrics`,
				`disabling_metrics_logging_after_page_going_invisible`
			);
		}

		hasBeenInvisibleRef.current = !isVisible || hasBeenInvisibleRef.current;
	}, [isVisible]);

	const getMetricsId = () => {
		try {
			let metricsId = localStorage.getItem(
				METRICS_CLIENT_UNIQUENESS_ID_STORAGE_KEY
			);

			if (!metricsId) {
				metricsId = window.crypto.randomUUID();
				localStorage.setItem(
					METRICS_CLIENT_UNIQUENESS_ID_STORAGE_KEY,
					metricsId
				);
			}

			return metricsId;
		} catch (e) {
			if (process.env.NEXT_PUBLIC_ISLOCAL) {
				return 'localdev';
			}

			// If this errors then we're not in a secure context (https) which Crypto.randomUUID requires
			return undefined;
		}
	};

	/**
	 * Helper to check if a metric should be sent when idle
	 */
	const shouldSendMetricWhenIdle = useCallback(
		(metricType: IdleAllowedMetricType) => {
			if (!hasBeenInvisibleRef.current) return true;
			return METRICS_TO_ALLOW_AFTER_IDLE.includes(metricType);
		},
		[]
	);

	/**
	 * This hook handles messages which should be sent regardless of whether the session is a sample session
	 */
	useEffect(() => {
		if (!metricsMessageBus) return;
		if (!messageBusMetricsEnabled(oldGrafanaMetricsFlagEnabled)) return;

		const subscription = metricsMessageBus.subscribe((message) => {
			switch (message.type) {
				case 'transaction_error_metric': {
					switch (message.value?.class) {
						case 'transaction_timeout': {
							if (!shouldSendMetricWhenIdle('transaction_timeout')) {
								dlog(`ui_metrics`, `skipping_transaction_timeout_because_idle`);
								return;
							}
							dlog(`ui_metrics`, `metrics_transaction_timeout`, {
								count: 1,
								preFlightEnabled: !getFeatureFlagsStore()?.flagEnabled(
									'SKIP_TRANSACTION_PREFLIGHTS'
								),
							});
							captureEvent('transaction_timeout', {
								count: 1,
								preFlightEnabled: !getFeatureFlagsStore()?.flagEnabled(
									'SKIP_TRANSACTION_PREFLIGHTS'
								),
							});
							break;
						}
						case 'transaction_cu_budget_exceeded':
						case 'transaction_other_error': {
							if (!shouldSendMetricWhenIdle('transaction_error')) {
								dlog(`ui_metrics`, `skipping_transaction_error_because_idle`);
								return;
							}
							dlog(`ui_metrics`, `metrics_transaction_error`, {
								errorClass: message?.value?.class,
								errorId: message?.value?.id,
							});
							captureEvent('transaction_error', {
								errorClass: message?.value?.class,
								errorId: message?.value?.id,
							});
							break;
						}
					}
					break;
				}
				case 'transaction_confirmation_time': {
					if (!shouldSendMetricWhenIdle('transaction_confirmation')) {
						dlog(
							`ui_metrics`,
							`skipping_transaction_confirmation_because_idle`
						);
						return;
					}
					dlog(`ui_metrics`, `metrics_transaction_confirmation`, {
						confirmationTimeMs: message.value,
						preFlightEnabled: !getFeatureFlagsStore()?.flagEnabled(
							'SKIP_TRANSACTION_PREFLIGHTS'
						),
					});
					captureEvent('transaction_confirmation', {
						confirmationTimeMs: message.value,
						preFlightEnabled: !getFeatureFlagsStore()?.flagEnabled(
							'SKIP_TRANSACTION_PREFLIGHTS'
						),
					});
					break;
				}
				case 'transaction_cu_budget_exceeded_metadata': {
					if (!shouldSendMetricWhenIdle('cu_budget_exceeded')) {
						dlog(`ui_metrics`, `skipping_cu_budget_exceeded_because_idle`);
						return;
					}
					dlog(`ui_metrics`, `metrics_cu_budget_exceeded`, message.value);
					captureEvent('cu_budget_exceeded', {
						programInstruction: message.value?.programInstruction,
						clientMethod: message.value?.clientMethod,
					});
					break;
				}
				case 'pre_prepped_transaction': {
					dlog(`ui_metrics`, `metrics_pre_prepped_transaction`, message.value);
					captureEvent('pre_prepped_transaction', {
						txType: message.value?.txType,
						usingPrePrepped: message.value?.usingPrePrepped,
					});
					break;
				}
				case 'passthrough_metric': {
					if (METRICS_SAMPLING_LOGGING_ENABLED) {
						// Handle startup time metrics specifically
						if (message.value?.startupTimeMetrics) {
							const startupMetrics = message.value.startupTimeMetrics;
							Object.entries(startupMetrics).forEach(([key, timeMs]) => {
								if (timeMs !== undefined) {
									dlog(`ui_metrics`, `metrics_startup_time`, {
										metricKey: key,
										timeMs,
									});
									captureEvent('startup_time', {
										metricKey: key,
										timeMs,
									});
								}
							});
						}
					}
					break;
				}
			}
		});

		return () => {
			subscription.unsubscribe();
		};
	}, [
		metricsMessageBus,
		captureEvent,
		shouldSendMetricWhenIdle,
		oldGrafanaMetricsFlagEnabled,
	]);

	/**
	 * This hook handles sending the background metrics, only for sample sessions
	 */
	useEffect(() => {
		if (!METRICS_SAMPLING_LOGGING_ENABLED) return;

		const interval = setInterval(() => {
			// Skip if page has been idle
			if (hasBeenInvisibleRef.current) {
				dlog(`ui_metrics`, `skipping_performance_snapshot_because_idle`);
				// Reset the idle flag if page is visible again
				if (isVisibleRef.current) {
					hasBeenInvisibleRef.current = false;
				}
				return;
			}

			// Pull out unconsumed data
			const performanceSnapshot = {
				requestsPerMinute:
					window.UNCONSUMED_GRAFANA_METRICS.AVG_NETWORK_REQUESTS_PER_MINUTE,
				maxSlotGapMs: window.UNCONSUMED_GRAFANA_METRICS.MAX_SLOT_GAP_MS,
				maxSlotGapCount: window.UNCONSUMED_GRAFANA_METRICS.MAX_SLOT_GAP_COUNT,
				// @ts-ignore - memory API is non-standard
				totalJSHeapSize: window.performance?.memory?.totalJSHeapSize,
				// @ts-ignore - memory API is non-standard
				usedJSHeapSize: window.performance?.memory?.usedJSHeapSize,
				eventLoopLag: window.UNCONSUMED_GRAFANA_METRICS.EVENT_LOOP_LAG,
				dlobSource:
					window.UNCONSUMED_GRAFANA_METRICS?.SELECTED_MARKET_DLOB_SOURCE &&
					window.UNCONSUMED_GRAFANA_METRICS?.SELECTED_MARKET_ID
						? {
								source:
									window.UNCONSUMED_GRAFANA_METRICS.SELECTED_MARKET_DLOB_SOURCE,
								marketId: window.UNCONSUMED_GRAFANA_METRICS.SELECTED_MARKET_ID,
							}
						: undefined,
				primarySubscriptionMethod: getPrimarySubscriptionMethod(),
				metricsId: getMetricsId(),
				historyServerRequestsTarget:
					window.UNCONSUMED_GRAFANA_METRICS.HISTORY_SERVER_REQUESTS_TARGET,
				historyServerRequestsSource:
					window.UNCONSUMED_GRAFANA_METRICS.HISTORY_SERVER_REQUESTS_SOURCE,
			};

			// Reset unconsumed data in window object
			window.UNCONSUMED_GRAFANA_METRICS = {};

			dlog(`ui_metrics`, `metrics_performance_snapshot`, performanceSnapshot);
			captureEvent('performance_snapshot', performanceSnapshot);

			// Reset the has been invisible flag for the next interval
			if (isVisibleRef.current) {
				hasBeenInvisibleRef.current = false;
			}
		}, METRICS_LOGGING_FREQUENCY);

		return () => {
			clearInterval(interval);
		};
	}, [captureEvent]);
};

export default singletonHook(undefined, useLogMetricsToPostHog);
